import { TypeIcon } from "../components/common/TypeIcon";
import { usePageBar } from "../contexts/PageBarContext";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { collection, deleteField, doc, getDocs, query, serverTimestamp, setDoc, where } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useAllTypeSettings, useTypeSettingsLoaded } from "../contexts/TypeSettingsContext";
import { ENTITY_TYPES, TIME_INPUT_MODES, storedEntityTypes } from "../types";
import type { CustomQuestion, Entity, EntityType, EventOption, TimeInputMode, TypeSettings } from "../types";
import { entityTypeLabel } from "../utils/tripDisplay";
import { fireWrite } from "../utils/offlineWrites";
import { getCustomQuestions, normalizeCustomQuestions } from "../utils/customQuestions";
import {
  TIME_INPUT_HINTS,
  TIME_INPUT_LABELS,
  builtInEventOptions,
  builtInTimeInputMode,
  typeCustomQuestions,
} from "../config/typeSettings";
import {
  activityRoleTypesFor,
  featuredActivityRolesFor,
  parseRoleList,
  type RoleTypeOption,
} from "../config/peopleRoleTypes";
import { CustomQuestionEditor } from "../components/entities/CustomQuestionEditor";
import { EventOptionsEditor } from "../components/entities/EventOptionsEditor";
import { RoleChecklist } from "../components/interactions/PeopleEditor";

/** Which parts of a type's settings have been saved (rather than built in). */
function customisedParts(s: TypeSettings | undefined): string[] {
  if (!s) return [];
  const parts: string[] = [];
  if (Array.isArray(s.eventOptions) && s.eventOptions.length > 0) parts.push("steps");
  if (s.timeInputMode) parts.push("time input");
  if (typeCustomQuestions(s).length > 0) parts.push("questions");
  if (parseRoleList(s.staffRoles)) parts.push("staff roles");
  return parts;
}

/** Every activity type, and which have defaults of their own. */
export function TypeDefaultsList() {
  const all = useAllTypeSettings();
  usePageBar({ title: "Type defaults", parent: { to: "/entities", label: "Entities" } });
  return (
    <div className="page">
      <div className="section-header">
        <h1>Type defaults</h1>
      </div>
      <p className="form-hint">
        What every activity of a type starts with — its log steps, time input, questions and staff roles. A place
        can still set its own on its edit page, and that wins over these.
      </p>
      <ul className="type-defaults-list">
        {ENTITY_TYPES.map((t) => {
          const parts = customisedParts(all[t]);
          return (
            <li key={t}>
              <Link to={`/types/${t}`} className="type-defaults-row">
                <span className="type-defaults-icon"><TypeIcon type={t} /></span>
                <span className="type-defaults-name">{entityTypeLabel(t)}</span>
                <span className="type-defaults-state">
                  {parts.length > 0 ? `Custom ${parts.join(", ")}` : "Built-in"}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** How many places of a type set each thing themselves, and so don't follow the type. */
interface OverrideCounts {
  total: number;
  steps: number;
  time: number;
  questions: number;
  roles: number;
}

function useOverrideCounts(type: EntityType): OverrideCounts | null {
  const [counts, setCounts] = useState<OverrideCounts | null>(null);
  useEffect(() => {
    let cancelled = false;
    setCounts(null);
    getDocs(query(collection(db, "entities"), where("type", "in", storedEntityTypes([type]))))
      .then((snap) => {
        if (cancelled) return;
        const c: OverrideCounts = { total: snap.size, steps: 0, time: 0, questions: 0, roles: 0 };
        snap.forEach((d) => {
          const e = d.data() as Entity;
          if ((e.eventOptions?.length ?? 0) > 0) c.steps++;
          if (e.customConfig?.time_input_mode) c.time++;
          if (getCustomQuestions(e).length > 0) c.questions++;
          if (parseRoleList(e.customConfig?.staff_roles)) c.roles++;
        });
        setCounts(c);
      })
      .catch(() => {
        if (!cancelled) setCounts(null);
      });
    return () => {
      cancelled = true;
    };
  }, [type]);
  return counts;
}

function OverrideNote({ count, total, what }: { count: number | undefined; total: number | undefined; what: string }) {
  if (!count || !total) return null;
  return (
    <span className="form-hint type-defaults-override">
      {count} of {total} places of this type set their own {what}, and keep them.
    </span>
  );
}

/** One type's defaults, edited. */
export function TypeDefaults() {
  const { type: rawType } = useParams<{ type: string }>();
  const loaded = useTypeSettingsLoaded();
  const all = useAllTypeSettings();
  const type = (ENTITY_TYPES as readonly string[]).includes(rawType ?? "") ? (rawType as EntityType) : null;
  usePageBar({
    title: type ? `${entityTypeLabel(type)} defaults` : null,
    parent: { to: "/types", label: "Type defaults" },
  });

  if (!type) {
    return (
      <div className="page">
        <p>Unknown type.</p>
        <Link to="/types">All types</Link>
      </div>
    );
  }
  if (!loaded) return <div className="loading">Loading...</div>;
  // Keyed on the type so the form starts over from that type's saved settings.
  return <TypeDefaultsForm key={type} type={type} saved={all[type]} />;
}

function TypeDefaultsForm({ type, saved }: { type: EntityType; saved: TypeSettings | undefined }) {
  const { userProfile } = useAuth();
  const canManage = !!userProfile?.roles?.includes("manage_entities");
  const counts = useOverrideCounts(type);
  const label = entityTypeLabel(type);

  const builtInMode = builtInTimeInputMode(type);
  const builtInSteps = builtInEventOptions(type);

  // "" (time) / [] (steps) / null (roles): use what's built in.
  const [timeMode, setTimeMode] = useState<TimeInputMode | "">(
    saved?.timeInputMode && (TIME_INPUT_MODES as readonly string[]).includes(saved.timeInputMode)
      ? saved.timeInputMode
      : "",
  );
  const [questions, setQuestions] = useState<CustomQuestion[]>(() => typeCustomQuestions(saved));
  const [steps, setSteps] = useState<EventOption[]>(() =>
    Array.isArray(saved?.eventOptions) ? saved.eventOptions : [],
  );
  const savedRoles = parseRoleList(saved?.staffRoles);
  const [rolesCustom, setRolesCustom] = useState(savedRoles !== null);
  const [roleOptions, setRoleOptions] = useState<RoleTypeOption[]>(() => {
    const builtIn = activityRoleTypesFor(type);
    if (!savedRoles) return builtIn;
    const seen = new Set(savedRoles.map((r) => r.value));
    return [...savedRoles, ...builtIn.filter((r) => !seen.has(r.value))];
  });
  const builtInKept = () =>
    new Set(featuredActivityRolesFor(type) ?? activityRoleTypesFor(type).map((r) => r.value));
  const [keptRoles, setKeptRoles] = useState<Set<string>>(() =>
    savedRoles ? new Set(savedRoles.map((r) => r.value)) : builtInKept(),
  );
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const save = () => {
    const cleanSteps = steps
      .filter((o) => o.displayName.trim())
      .map((o) => ({ ...o, displayName: o.displayName.trim() }));
    const cleanQuestions = normalizeCustomQuestions(questions);
    const roles = roleOptions.filter((r) => keptRoles.has(r.value));
    fireWrite(
      setDoc(
        doc(db, "typeSettings", type),
        {
          timeInputMode: timeMode || deleteField(),
          customQuestions: cleanQuestions.length > 0 ? cleanQuestions : deleteField(),
          eventOptions: cleanSteps.length > 0 ? cleanSteps : deleteField(),
          staffRoles: rolesCustom && roles.length > 0 ? roles : deleteField(),
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      ),
      "save type defaults",
    );
    setSavedAt(Date.now());
  };

  return (
    <div className="page type-defaults">
      <div className="section-header">
        <h1>
          <TypeIcon type={type} /> {label} defaults
        </h1>
      </div>
      <p className="form-hint">
        Every {label} activity starts with these: places of this type, and activities with no place that are typed{" "}
        {label}. A place can set its own on its edit page instead.
      </p>
      {!canManage && <p className="form-hint">Only people who can manage entities can change these.</p>}

      <fieldset className="type-defaults-fields" disabled={!canManage}>
        <section className="form-field">
          <span className="form-label">Time input</span>
          <select
            className="form-input"
            value={timeMode}
            onChange={(e) => setTimeMode(e.target.value as TimeInputMode | "")}
          >
            <option value="">Built-in ({TIME_INPUT_LABELS[builtInMode]})</option>
            {TIME_INPUT_MODES.map((m) => (
              <option key={m} value={m}>{TIME_INPUT_LABELS[m]}</option>
            ))}
          </select>
          <span className="form-hint">{TIME_INPUT_HINTS[timeMode || builtInMode]}</span>
          <OverrideNote count={counts?.time} total={counts?.total} what="time input" />
        </section>

        <section className="form-field">
          <CustomQuestionEditor questions={questions} onChange={setQuestions} />
          <OverrideNote count={counts?.questions} total={counts?.total} what="questions" />
        </section>

        <section className="form-field">
          <EventOptionsEditor
            options={steps}
            setOptions={setSteps}
            flowDefaults={builtInSteps}
            defaultsToLoad={builtInSteps}
            typeName={`built-in ${label}`}
            entityType={type}
            hint={
              <>
                The one-tap steps offered when logging a {label} activity. Tap a step to rename it or put the buttons
                after it in order.
              </>
            }
          />
          <OverrideNote count={counts?.steps} total={counts?.total} what="steps" />
        </section>

        <section className="form-field">
          <span className="form-label">Staff roles</span>
          <span className="form-hint">
            The roles offered up front when adding staff & crew. The rest stay under More….
          </span>
          <RoleChecklist
            options={roleOptions}
            onOptionsChange={(o) => {
              setRoleOptions(o);
              setRolesCustom(true);
            }}
            kept={keptRoles}
            onKeptChange={(k) => {
              setKeptRoles(k);
              setRolesCustom(true);
            }}
          />
          {rolesCustom && (
            <button
              type="button"
              className="btn-link"
              onClick={() => {
                setRolesCustom(false);
                setRoleOptions(activityRoleTypesFor(type));
                setKeptRoles(builtInKept());
              }}
            >
              Back to the built-in roles
            </button>
          )}
          <OverrideNote count={counts?.roles} total={counts?.total} what="staff roles" />
        </section>
      </fieldset>

      {canManage && (
        <div className="form-actions type-defaults-actions">
          <button type="button" className="btn" onClick={save}>
            Save {label} defaults
          </button>
          {savedAt && <span className="live-notes-status live-notes-status-saved">Saved</span>}
        </div>
      )}
    </div>
  );
}
